import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { publicCpfSignInRequest } from 'store/modules/auth/actions';
import CompanyCustomizationData from 'services/dataService/CompanyCustomizationData';
import PublicCpfAccessData from 'services/dataService/PublicCpfAccessData';
import { CpfMaskCustom } from 'components/CpfMaskCustom';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f5f7fa',
  },
  header: {
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 14),
    backgroundColor: '#fff',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 3),
    },
  },
  logo: {
    maxWidth: 170,
    maxHeight: 38,
    objectFit: 'contain',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(2),
    },
  },
  navLink: {
    color: '#111',
    fontSize: 16,
    fontWeight: 500,
  },
  hero: {
    minHeight: 'calc(100vh - 60px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(4, 12),
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundColor: '#f5f7fa',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      padding: theme.spacing(6, 3, 4),
      backgroundPosition: 'center bottom',
    },
  },
  card: {
    width: 420,
    maxWidth: '100%',
    padding: theme.spacing(4.5),
    borderRadius: 8,
    backgroundColor: 'rgba(247, 242, 242, 0.85)',
    border: '1px solid rgba(17, 24, 39, 0.08)',
    boxShadow: '0 18px 40px rgba(17, 24, 39, 0.3)',
    color: '#1f2933',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3),
    },
  },
  title: {
    marginBottom: theme.spacing(3),
    textAlign: 'center',
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: 0,
    color: '#273142',
  },
  field: {
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9fafb',
      borderRadius: 7,
      color: '#273142',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d8dee8',
    },
    '& .MuiInputAdornment-root': {
      color: '#8a94a6',
    },
    '& input::placeholder': {
      color: '#8a94a6',
      opacity: 1,
    },
  },
  button: {
    height: 50,
    borderRadius: 7,
    fontWeight: 700,
    '&.Mui-disabled': {
      backgroundColor: '#edf0f5',
      color: '#8a94a6',
    },
  },
  terms: {
    display: 'block',
    marginTop: theme.spacing(3),
    textAlign: 'center',
    color: '#5f6b7a',
    fontSize: 12,
  },
  termsLink: {
    color: '#1e88ff',
    fontWeight: 700,
  },
}));

export default function PublicCpfLogin() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { companyKey } = useParams();
  const loadingAuth = useSelector(state => state.auth.loading);
  const [cpf, setCpf] = useState('');
  const [company, setCompany] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [loading, setLoading] = useState(true);

  const domain = window.location.hostname;

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const response = await PublicCpfAccessData.show(companyKey);
        const currentCompany = response.data.company;
        let customizationData = null;

        try {
          const customizationResponse = await CompanyCustomizationData.getCompanyCustomization(
            currentCompany.id
          );
          customizationData = customizationResponse.data;
        } catch (err) {
          customizationData = null;
        }

        if (!mounted) return;
        setCompany(currentCompany);
        setCustomization(customizationData);
      } catch (err) {
        if (mounted) history.replace('/not-found');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [companyKey, history]);

  const bannerUrl = useMemo(
    () => customization?.company_banner?.banner?.url,
    [customization]
  );

  const logoUrl = useMemo(
    () => customization?.company_header?.logo?.url || company?.logo?.url,
    [company, customization]
  );

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      dispatch(publicCpfSignInRequest(companyKey, cpf, domain));
    },
    [companyKey, cpf, dispatch, domain]
  );

  const cpfDigits = cpf.replace(/[^\d]/g, '');

  if (loading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        {logoUrl ? (
          <img alt={company?.name || 'Logo'} className={classes.logo} src={logoUrl} />
        ) : (
          <span />
        )}

        <nav className={classes.nav}>
          <Link className={classes.navLink} href="/validate" underline="none">
            Certificado
          </Link>
          <Link className={classes.navLink} href="/login" underline="none">
            Acesse agora
          </Link>
        </nav>
      </header>

      <main
        className={classes.hero}
        style={{
          backgroundImage: bannerUrl ? `url(${bannerUrl})` : 'none',
        }}
      >
        <form className={classes.card} onSubmit={handleSubmit}>
          <Typography className={classes.title} component="h1">
            ACESSE AGORA
          </Typography>

          <TextField
            autoFocus
            className={classes.field}
            fullWidth
            name="cpf"
            onChange={event => setCpf(event.target.value)}
            placeholder="CPF"
            InputProps={{
              inputComponent: CpfMaskCustom,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon />
                </InputAdornment>
              ),
            }}
            value={cpf}
            variant="outlined"
          />

          <Button
            className={classes.button}
            color="primary"
            disabled={loadingAuth || cpfDigits.length !== 11}
            fullWidth
            type="submit"
            variant="contained"
          >
            {loadingAuth ? 'ENTRANDO...' : 'ENTRAR'}
          </Button>

          <Typography className={classes.terms}>
            Ao realizar o login você aceita os nossos{' '}
            <Link className={classes.termsLink} href="/terms" underline="none">
              termos de uso
            </Link>
          </Typography>
        </form>
      </main>
    </div>
  );
}
